import {FC, useState, useEffect, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {Modal, Card, Badge, Button} from 'react-bootstrap'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {KTIcon} from '../../../../_metronic/helpers'
import {Project} from '../mockData'
import {get_single_project} from '../api/projectsApi'
import {toast} from 'react-toastify'
import {LoadingProgressBar} from './LoadingProgressBar'
import {ProjectDetailSkeleton} from './ProjectDetailSkeleton'
import {
  get_all_subprojects,
  create_subproject,
  update_subproject,
  delete_subproject
} from '../api/subprojectsApi'
import {permissionCheck} from '../../../helper-functions/PermissionCheck'
import AssetDownloadModal from '../../digital-assets/components/AssetDownloadModal'
import {Asset, AssetCategory} from '../../digital-assets/types/assetTypes'

const ProjectDetail: FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const projectIdRef = useRef<string | undefined>(id) // Store current project ID in ref
  const [project, setProject] = useState<Project | null>(null)
  const [projectData, setProjectData] = useState<any>(null) // Store full API response
  const [isLoading, setIsLoading] = useState(true)
  
  // Subprojects state
  const [subprojects, setSubprojects] = useState<any[]>([])
  const [isSubprojectLoading, setIsSubprojectLoading] = useState(true)
  const [showSubprojectModal, setShowSubprojectModal] = useState(false)
  const [showDeleteSubprojectModal, setShowDeleteSubprojectModal] = useState(false)
  const [editingSubproject, setEditingSubproject] = useState<any | null>(null)
  const [subprojectToDelete, setSubprojectToDelete] = useState<any | null>(null)
  const [subprojectCurrentPage, setSubprojectCurrentPage] = useState(1)
  const [subprojectPageSize, setSubprojectPageSize] = useState(5)
  const [subprojectForm, setSubprojectForm] = useState({
    subproject_name: '',
    description: '',
    status: '1' as '1' | '0',
  })
  
  // Permission checks (subprojects use project permissions)
  const canCreateSubproject = Boolean(permissionCheck('subproject-create'))
  const canUpdateSubproject = Boolean(permissionCheck('subproject-update'))
  const canDeleteSubproject = Boolean(permissionCheck('subproject-delete'))
  const canViewAssets = Boolean(permissionCheck('upload-view'))
  const canDownloadAsset = Boolean(permissionCheck('download-view'))
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)

  // Update ref when id changes
  useEffect(() => {
    projectIdRef.current = id
  }, [id])

  useEffect(() => {
    if (id) {
      fetchProjectDetail()
      fetchProjectSubprojects(id)
    }
  }, [id])

  const fetchProjectDetail = async () => {
    try {
      setIsLoading(true)
      const response = await get_single_project(id || '')
      
      // API response structure: { status: true, message: "...", data: { project: {...} } }
      // Or: { status: true, message: "...", data: { all_projects: [...] } } - single item
      const apiProject = response?.data?.project || (response?.data?.all_projects && Array.isArray(response.data.all_projects) ? response.data.all_projects[0] : null)
      
      if (apiProject) {
        // Store full API response for counts
        setProjectData(apiProject)
        
        // Map API response to UI format
        const mappedProject: Project = {
          id: apiProject.id.toString(),
          name: apiProject.project_name || '',
          description: apiProject.description || '',
          status: (apiProject.status === 1 || apiProject.status === '1') ? 'active' : 'inactive',
          createdAt: apiProject.created_at || '',
          updatedAt: apiProject.updated_at || '',
          totalUrls: 0,
          activeUrls: 0,
          isStarred: false,
          category: '',
          tags: []
        }
        setProject(mappedProject)
        
        // Set subprojects from API response if available
        if (apiProject.subprojects && Array.isArray(apiProject.subprojects)) {
          setSubprojects(apiProject.subprojects)
        }
      } else {
        toast.error('Project not found')
        setProject(null)
        setProjectData(null)
      }
    } catch (error) {
      console.error('Error fetching project:', error)
      toast.error('Failed to load project details')
      setProject(null)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchProjectSubprojects = async (projectId: string) => {
    try {
      setIsSubprojectLoading(true)
      const response = await get_all_subprojects(projectId)
      // Expected: { status: true, message: "...", data: { all_subprojects: [...] } }
      const apiSubprojects = response?.data?.subprojects || response?.data?.all_subprojects || response?.data || []
      const subprojectsList = Array.isArray(apiSubprojects) ? apiSubprojects : []
      
      // Filter subprojects by current project_id to ensure we only show subprojects for this project
      const projectIdNum = Number(projectId)
      const filteredSubprojects = subprojectsList.filter((sp: any) => {
        const spProjectId = Number(sp.project_id)
        return spProjectId === projectIdNum
      })
      
      setSubprojects(filteredSubprojects)
      setSubprojectCurrentPage(1)
    } catch (error) {
      console.error('Error fetching subprojects:', error)
      // If API doesn't exist yet, try to get from project data
      try {
        const projectResponse = await get_single_project(projectId)
        const apiProject = projectResponse?.data?.project || (projectResponse?.data?.all_projects && Array.isArray(projectResponse.data.all_projects) ? projectResponse.data.all_projects[0] : null)
        if (apiProject?.subprojects && Array.isArray(apiProject.subprojects)) {
          setSubprojects(apiProject.subprojects)
        } else {
          setSubprojects([])
        }
      } catch (e) {
        console.error('Error getting subprojects from project:', e)
        setSubprojects([])
      }
    } finally {
      setIsSubprojectLoading(false)
    }
  }

  // Subprojects handlers
  const openNewSubprojectModal = () => {
    setEditingSubproject(null)
    setSubprojectForm({ subproject_name: '', description: '', status: '1' })
    setShowSubprojectModal(true)
  }

  const openEditSubprojectModal = (subproject: any) => {
    setEditingSubproject(subproject)
    setSubprojectForm({
      subproject_name: subproject.subproject_name || subproject.title || '',
      description: subproject.description || '',
      status: (subproject.status === 1 || subproject.status === '1') ? '1' : '0'
    })
    setShowSubprojectModal(true)
  }

  const handleSaveSubproject = async () => {
    if (!project) return
    
    // Get current project ID from ref (always up-to-date)
    const currentProjectId = projectIdRef.current
    if (!currentProjectId) {
      toast.error('Project ID not found')
      return
    }
    
    if (!subprojectForm.subproject_name.trim()) {
      toast.error('Subproject name is required')
      return
    }
    
    try {
      const payload = {
        subproject_name: subprojectForm.subproject_name.trim(),
        description: subprojectForm.description.trim(),
        status: subprojectForm.status
      }
      if (editingSubproject?.id) {
        await update_subproject(editingSubproject.id.toString(), payload)
        toast.success('Subproject updated successfully')
      } else {
        await create_subproject({
          project_id: currentProjectId,
          ...payload
        })
        toast.success('Subproject created successfully')
      }
      setShowSubprojectModal(false)
      setEditingSubproject(null)
      // Refresh project detail to get updated counts
      await fetchProjectDetail()
      // Use the current project ID from ref to fetch subprojects
      await fetchProjectSubprojects(currentProjectId)
    } catch (error: any) {
      console.error('Error saving subproject:', error)
      toast.error(error?.response?.data?.message || 'Failed to save subproject')
    }
  }

  const handleDeleteSubprojectClick = (subproject: any) => {
    setSubprojectToDelete(subproject)
    setShowDeleteSubprojectModal(true)
  }

  const handleDeleteSubprojectConfirm = async () => {
    if (!subprojectToDelete) return
    
    // Get current project ID from ref (always up-to-date)
    const currentProjectId = projectIdRef.current
    if (!currentProjectId) {
      toast.error('Project ID not found')
      return
    }
    
    try {
      if (subprojectToDelete.id) {
        await delete_subproject(subprojectToDelete.id.toString())
        toast.success('Subproject deleted successfully')
      } else {
        // If no ID, just remove from local state (for newly added subprojects)
        setSubprojects(prev => prev.filter(sp => sp !== subprojectToDelete))
        toast.success('Subproject removed')
      }
      // Refresh project detail to get updated counts
      await fetchProjectDetail()
      // Use the current project ID from ref to fetch subprojects
      await fetchProjectSubprojects(currentProjectId)
      setShowDeleteSubprojectModal(false)
      setSubprojectToDelete(null)
    } catch (error: any) {
      console.error('Error deleting subproject:', error)
      toast.error(error?.response?.data?.message || 'Failed to delete subproject')
    }
  }

  const handleViewSubprojectDetails = (subproject: any) => {
    if (!subproject.id) {
      toast.error('Subproject ID not found')
      return
    }
    // Navigate to subproject detail page
    navigate(`/projects/subproject/${subproject.id}`)
  }

  // Show skeleton while loading
  if (isLoading && !project) {
    return <ProjectDetailSkeleton />
  }

  if (!project) {
    return (
      <div className='d-flex flex-column flex-column-fluid'>
        <div className='text-center py-10'>
          <KTIcon iconName='abstract-26' className='fs-3x text-muted mb-5' />
          <h3 className='text-gray-900 mb-3'>Project not found</h3>
          <button 
            className='btn btn-primary d-flex align-items-center'
            onClick={() => navigate('/projects/list')}
            style={{ cursor: 'pointer' }}
          >
            <KTIcon iconName='arrow-left' className='fs-2 me-2' />
            <span>Back to Projects</span>
          </button>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: {class: 'badge-light-success', text: 'Active'},
      inactive: {class: 'badge-light-danger', text: 'Inactive'},
      draft: {class: 'badge-light-warning', text: 'Draft'}
    }
    
    const config = statusConfig[status as keyof typeof statusConfig]
    return (
      <span className={`badge ${config.class} fw-bold`}>
        {config.text}
      </span>
    )
  }

  // Helper function to get thumbnail URL from file path
  const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
    if (!filePath) return null;
    
    // If already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // Construct full URL from base URL
    let apiBaseUrl = process.env.REACT_APP_API_URL || '';
    // Remove /api suffix if present, but keep the base domain
    apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    // Ensure base URL doesn't end with slash
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    
    // Handle file path - remove leading slash if present, then add it back
    const cleanPath = filePath.replace(/^\//, '');
    
    return `${apiBaseUrl}/${cleanPath}`;
  };

  // Helper function to get category badge variant
  const getCategoryBadgeVariant = (categoryName: string | undefined): string => {
    if (!categoryName) return 'secondary';
    const name = categoryName.toLowerCase();
    if (name.includes('video template')) return 'primary';
    if (name.includes('stock video') || name.includes('videos')) return 'success';
    if (name.includes('audio')) return 'warning';
    return 'info';
  };

  // Helper function to format category label
  const getCategoryLabel = (categoryName: string | undefined): string => {
    if (!categoryName) return 'Unknown';
    return categoryName;
  };

  // Helper function to format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Extract all uploads from subprojects
  const getAllUploads = (): any[] => {
    if (!projectData?.subprojects || !Array.isArray(projectData.subprojects)) {
      return [];
    }
    
    const allUploads: any[] = [];
    projectData.subprojects.forEach((subproject: any) => {
      if (subproject.upload && Array.isArray(subproject.upload)) {
        subproject.upload.forEach((upload: any) => {
          allUploads.push(upload);
        });
      }
    });
    
    return allUploads;
  };

  const handleViewAsset = (uploadId: number) => {
    if (canViewAssets && uploadId) {
      navigate(`/digital-assets/view/${uploadId}`);
    }
  };

  const handleDownloadAsset = (upload: any) => {
    if (!upload.asset_file_path || upload.asset_file_path === null || upload.asset_file_path.trim() === '') {
      toast.warning('File is not uploaded yet. Please upload the file first before downloading.');
      return;
    }
    
    const assetData: Asset = {
      id: upload.id,
      name: upload.upload_name || upload.name || '',
      category: upload.category?.category_name 
        ? (upload.category.category_name.toLowerCase().includes('video template') 
            ? AssetCategory.VIDEO_TEMPLATES
            : upload.category.category_name.toLowerCase().includes('stock video')
            ? AssetCategory.STOCK_VIDEOS
            : upload.category.category_name.toLowerCase().includes('audio')
            ? AssetCategory.AUDIOS
            : AssetCategory.GRAPHICS)
        : AssetCategory.GRAPHICS,
      description: upload.description || '',
      tags: upload.tags?.map((t: any) => t.tag_title || t) || [],
      thumbnails: upload.primary_thumbnail?.file_path ? [getThumbnailUrl(upload.primary_thumbnail.file_path) || ''] : [],
      zip_file_url: upload.asset_file_path || '',
      licenses: [],
      metadata: {
        size: 0,
        file_type: 'zip',
        mime_type: 'application/zip',
      },
      source_platform: upload.platform?.platform_name as any,
      uploaded_at: upload.created_at || '',
      status: 'completed',
    };
    
    setSelectedAsset(assetData);
    setShowDownloadModal(true);
  };

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={isLoading || isSubprojectLoading} />
      
      {/* Header */}
      <div className='toolbar' id='kt_toolbar'>
        <div id='kt_toolbar_container' className='container-fluid d-flex flex-stack justify-content-start align-items-start'>
          <div className='page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0 flex-shrink-0'>
            <button
              className='btn btn-sm btn-light me-3 flex-shrink-0 d-flex align-items-center'
              onClick={() => navigate('/projects/list')}
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName='arrow-left' className='fs-2 me-2' />
              <span>Back</span>
            </button>
            <h1 className='d-flex align-items-center text-dark fw-bolder fs-3 my-1 flex-wrap'>
              <span className='me-2'>{project.name}</span>
            </h1>
          </div>
          <div className='d-flex align-items-center gap-2 gap-lg-3 flex-shrink-0 ms-auto'>
            {/* No buttons in header - all actions moved to respective sections */}
          </div>
        </div>
      </div>

      {/* Content */}
      <div id='kt_content' className='content flex-column-fluid'>
        <div className='container-xxl'>
          {/* Statistics Cards */}
          <div className='row g-5 g-xl-8 mb-5'>
            <div className='col-xl-3 col-lg-6 col-md-6'>
              <KTCard className='card-xl-stretch mb-xl-8 shadow-sm'>
                <KTCardBody className='d-flex flex-column p-6'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-60px me-4'>
                      <div className='symbol-label bg-light-primary'>
                        <KTIcon iconName='abstract-26' className='fs-2x text-primary' />
                      </div>
                    </div>
                    <div className='flex-grow-1'>
                      <div className='fs-2 fw-bolder text-gray-900 mb-1'>
                        {projectData?.total_subprojects_count ?? subprojects.length}
                      </div>
                      <div className='fs-7 text-muted fw-semibold'>Total Subprojects</div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>

            <div className='col-xl-3 col-lg-6 col-md-6'>
              <KTCard className='card-xl-stretch mb-xl-8 shadow-sm'>
                <KTCardBody className='d-flex flex-column p-6'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-60px me-4'>
                      <div className='symbol-label bg-light-success'>
                        <KTIcon iconName='check-circle' className='fs-2x text-success' />
                      </div>
                    </div>
                    <div className='flex-grow-1'>
                      <div className='fs-2 fw-bolder text-gray-900 mb-1'>
                        {projectData?.active_subprojects_count ?? subprojects.filter((sp: any) => sp.status === 1 || sp.status === '1' || sp.status === 'active').length}
                      </div>
                      <div className='fs-7 text-muted fw-semibold'>Active Subprojects</div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>

            <div className='col-xl-3 col-lg-6 col-md-6'>
              <KTCard className='card-xl-stretch mb-xl-8 shadow-sm'>
                <KTCardBody className='d-flex flex-column p-6'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-60px me-4'>
                      <div className='symbol-label bg-light-danger'>
                        <KTIcon iconName='cross-circle' className='fs-2x text-danger' />
                      </div>
                    </div>
                    <div className='flex-grow-1'>
                      <div className='fs-2 fw-bolder text-gray-900 mb-1'>
                        {projectData?.inactive_subprojects_count ?? subprojects.filter((sp: any) => !(sp.status === 1 || sp.status === '1' || sp.status === 'active')).length}
                      </div>
                      <div className='fs-7 text-muted fw-semibold'>Inactive Subprojects</div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>

            <div className='col-xl-3 col-lg-6 col-md-6'>
              <KTCard className='card-xl-stretch mb-xl-8 shadow-sm'>
                <KTCardBody className='d-flex flex-column p-6'>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-60px me-4'>
                      <div className='symbol-label bg-light-info'>
                        <KTIcon iconName='file-down' className='fs-2x text-info' />
                      </div>
                    </div>
                    <div className='flex-grow-1'>
                      <div className='fs-2 fw-bolder text-gray-900 mb-1'>
                        {projectData?.total_uploads_count ?? 0}
                      </div>
                      <div className='fs-7 text-muted fw-semibold'>Total Uploads</div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>
          </div>

          {/* Description Tile */}
          {project.description && (
            <div className='card mb-5'>
              <div className='card-body'>
                <div className='d-flex align-items-center mb-3'>
                  <KTIcon iconName='document' className='fs-2x text-primary me-3' />
                  <h3 className='fw-bolder text-gray-900 mb-0'>Description</h3>
                </div>
                <p className='text-gray-700 fs-6 mb-0' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {project.description}
                </p>
              </div>
            </div>
          )}

          {/* Project Info */}
          <div className='card mb-5'>
            <div className='card-body'>
              <div className='d-flex align-items-center justify-content-between mb-4'>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-60px me-4'>
                    <div className='symbol-label bg-light-primary'>
                      <KTIcon iconName='abstract-26' className='fs-2x text-primary' />
                    </div>
                  </div>
                  <div>
                    <h2 className='fs-2 fw-bolder text-gray-900 mb-1'>{project.name}</h2>
                    <div className='d-flex align-items-center gap-3'>
                      {getStatusBadge(project.status)}
                      <span className='text-muted fs-7'>
                        Created: {new Date(project.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Subprojects Section */}
          <div className='card mb-5'>
            <div className='card-header border-0 pt-6'>
              <div className='card-title'>
                <h3 className='fw-bolder mb-0'>Subprojects</h3>
              </div>
              <div className='card-toolbar'>
                {canCreateSubproject && (
                  <button className='btn btn-primary' onClick={openNewSubprojectModal}>
                    <KTIcon iconName='plus' className='fs-2' />
                    Add Subproject
                  </button>
                )}
              </div>
            </div>
            <div className='card-body pt-0'>
              {isSubprojectLoading ? (
                <div className='text-center text-muted py-10'>Loading subprojects...</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed fs-6 gy-5'>
                    <thead>
                      <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th className='text-end'>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='fw-semibold text-gray-600'>
                      {subprojects.length === 0 && (
                        <tr>
                          <td colSpan={4} className='text-center text-muted py-10'>No subprojects found</td>
                        </tr>
                      )}
                      {subprojects
                        .slice((subprojectCurrentPage - 1) * subprojectPageSize, (subprojectCurrentPage - 1) * subprojectPageSize + subprojectPageSize)
                        .map((subproject: any) => (
                        <tr key={subproject.id}>
                          <td className='fw-bold'>{subproject.title || subproject.subproject_name || 'Untitled'}</td>
                          <td>
                            <div 
                              className='text-gray-600' 
                              style={{
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={subproject.description || '-'}
                            >
                              {subproject.description || '-'}
                            </div>
                          </td>
                          <td>
                            {(subproject.status === 1 || subproject.status === '1' || subproject.status === 'active') ? (
                              <span className='badge badge-light-success fw-bold'>Active</span>
                            ) : (
                              <span className='badge badge-light-danger fw-bold'>Inactive</span>
                            )}
                          </td>
                          <td className='text-end'>
                            <button 
                              className='btn btn-sm btn-light me-2' 
                              onClick={() => handleViewSubprojectDetails(subproject)}
                              title='View Details'
                            >
                              <KTIcon iconName='eye' className='fs-5' />
                            </button>
                            {canUpdateSubproject && (
                              <button className='btn btn-sm btn-light me-2' onClick={() => openEditSubprojectModal(subproject)}>
                                <KTIcon iconName='pencil' className='fs-5' />
                              </button>
                            )}
                            {canDeleteSubproject && subproject.id && (
                              <button className='btn btn-sm btn-light-danger' onClick={() => handleDeleteSubprojectClick(subproject)}>
                                <KTIcon iconName='trash' className='fs-5' />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination controls */}
                  <div className='d-flex align-items-center justify-content-between mt-4'>
                    <div className='d-flex align-items-center gap-3'>
                      <span className='text-muted fs-7'>
                        {subprojects.length === 0
                          ? 'No records'
                          : `Showing ${(subprojectCurrentPage - 1) * subprojectPageSize + 1}-${Math.min(subprojectCurrentPage * subprojectPageSize, subprojects.length)} of ${subprojects.length}`}
                      </span>
                      <div className='d-flex align-items-center'>
                        <span className='me-2 text-muted fs-7'>Show</span>
                        <select
                          className='form-select form-select-sm w-auto'
                          value={subprojectPageSize}
                          onChange={(e) => setSubprojectPageSize(Number(e.target.value))}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                        </select>
                        <span className='ms-2 text-muted fs-7'>per page</span>
                      </div>
                    </div>
                    <ul className='pagination'>
                      <li className={`page-item ${subprojectCurrentPage === 1 ? 'disabled' : ''}`}>
                        <button className='page-link' onClick={() => setSubprojectCurrentPage(1)}>«</button>
                      </li>
                      <li className={`page-item ${subprojectCurrentPage === 1 ? 'disabled' : ''}`}>
                        <button className='page-link' onClick={() => setSubprojectCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
                      </li>
                      <li className='page-item active'>
                        <span className='page-link'>
                          {subprojectCurrentPage} / {Math.max(1, Math.ceil(subprojects.length / subprojectPageSize))}
                        </span>
                      </li>
                      <li className={`page-item ${subprojectCurrentPage >= Math.ceil(subprojects.length / subprojectPageSize) ? 'disabled' : ''}`}>
                        <button className='page-link' onClick={() => setSubprojectCurrentPage(p => Math.min(Math.max(1, Math.ceil(subprojects.length / subprojectPageSize)), p + 1))}>Next</button>
                      </li>
                      <li className={`page-item ${subprojectCurrentPage >= Math.ceil(subprojects.length / subprojectPageSize) ? 'disabled' : ''}`}>
                        <button className='page-link' onClick={() => setSubprojectCurrentPage(Math.max(1, Math.ceil(subprojects.length / subprojectPageSize)))}>»</button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assets Section */}
          {canViewAssets && (
            <div className='card mb-5'>
              <div className='card-header border-0 pt-6'>
                <div className='card-title'>
                  <h3 className='fw-bolder mb-0'>Project Assets</h3>
                </div>
              </div>
              <div className='card-body pt-0'>
                {(() => {
                  const allUploads = getAllUploads();
                  
                  if (allUploads.length === 0) {
                    return (
                      <div className='text-center py-5'>
                        <KTIcon iconName='picture' className='fs-3x text-muted mb-3' />
                        <p className='text-muted fs-6'>No assets found in this project</p>
                      </div>
                    );
                  }

                  return (
                    <div className='row g-4'>
                      {allUploads.map((upload: any, index: number) => {
                        // Get thumbnail URL
                        let thumbnailUrl: string | null = null;
                        if (upload.primary_thumbnail?.file_path) {
                          thumbnailUrl = getThumbnailUrl(upload.primary_thumbnail.file_path);
                        }

                        return (
                          <div key={upload.id || index} className='col-md-3 col-lg-3'>
                            <Card 
                              className='h-100 shadow-sm border-0 card-hover' 
                              style={{ transition: 'all 0.3s ease', cursor: canViewAssets ? 'pointer' : 'default' }}
                              onClick={canViewAssets ? () => handleViewAsset(upload.id) : undefined}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-5px)';
                                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <div className='position-relative'>
                                {thumbnailUrl ? (
                                  <Card.Img
                                    variant='top'
                                    src={thumbnailUrl}
                                    style={{ height: '220px', objectFit: 'cover' }}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23e0e0e0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" font-size="18" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                    }}
                                  />
                                ) : (
                                  <div 
                                    className='d-flex align-items-center justify-content-center bg-light'
                                    style={{ height: '220px' }}
                                  >
                                    <KTIcon iconName='picture' className='fs-1 text-muted' />
                                  </div>
                                )}
                                {/* Status Badge */}
                                <div className='position-absolute top-0 end-0 m-2'>
                                  {upload.asset_file_path && upload.asset_file_path !== null && upload.asset_file_path.trim() !== '' ? (
                                    <Badge bg='success' className='px-2 py-1'>
                                      <KTIcon iconName='check-circle' className='me-1 fs-7' />
                                      Ready
                                    </Badge>
                                  ) : (
                                    <Badge bg='warning' className='px-2 py-1'>
                                      <KTIcon iconName='clock' className='me-1 fs-7' />
                                      Pending
                                    </Badge>
                                  )}
                                </div>
                                {/* Category Badge */}
                                {upload.category?.category_name && (
                                  <div className='position-absolute top-0 start-0 m-2'>
                                    <Badge bg={getCategoryBadgeVariant(upload.category.category_name)} className='px-2 py-1'>
                                      {getCategoryLabel(upload.category.category_name)}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                              <Card.Body className='d-flex flex-column p-3'>
                                <Card.Title className='fs-6 fw-bold mb-2 text-truncate' title={upload.upload_name || upload.name}>
                                  {upload.upload_name || upload.name}
                                </Card.Title>
                                
                                {/* Description Preview */}
                                {upload.description && (
                                  <Card.Text className='text-muted small mb-2' style={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    minHeight: '40px'
                                  }}>
                                    {upload.description}
                                  </Card.Text>
                                )}

                                {/* Platform Badge */}
                                {upload.platform?.platform_name && (
                                  <div className='mb-2'>
                                    <Badge bg='info' className='px-2 py-1'>
                                      <KTIcon iconName='link' className='me-1 fs-8' />
                                      {upload.platform.platform_name}
                                    </Badge>
                                  </div>
                                )}

                                {/* Date */}
                                <div className='mb-2'>
                                  <div className='d-flex align-items-center text-muted small'>
                                    <KTIcon iconName='calendar' className='me-1 fs-7' />
                                    <span>{formatDate(upload.created_at)}</span>
                                  </div>
                                </div>

                                {/* Tags */}
                                {upload.tags && upload.tags.length > 0 && (
                                  <div className='mb-2'>
                                    {upload.tags.slice(0, 2).map((tag: any, tagIndex: number) => (
                                      <Badge key={tagIndex} bg='secondary' className='me-1 mb-1' style={{ fontSize: '10px' }}>
                                        {tag.tag_title || tag}
                                      </Badge>
                                    ))}
                                    {upload.tags.length > 2 && (
                                      <Badge bg='secondary' style={{ fontSize: '10px' }}>
                                        +{upload.tags.length - 2}
                                      </Badge>
                                    )}
                                  </div>
                                )}

                                {/* Action Buttons */}
                                <div className='d-flex gap-1 mt-auto pt-2 border-top' onClick={(e) => e.stopPropagation()}>
                                  {canViewAssets && (
                                    <Button
                                      variant='primary'
                                      size='sm'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewAsset(upload.id);
                                      }}
                                      className='flex-fill'
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    >
                                      <KTIcon iconName='eye' className='me-1 fs-7' />
                                      View
                                    </Button>
                                  )}
                                  {canDownloadAsset && upload.asset_file_path && upload.asset_file_path.trim() !== '' && (
                                    <Button
                                      variant='info'
                                      size='sm'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadAsset(upload);
                                      }}
                                      title='Download Asset'
                                      className='flex-fill'
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                    >
                                      <KTIcon iconName='file-down' className='me-1 fs-7' />
                                      Download
                                    </Button>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Download Modal */}
      <AssetDownloadModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
          setSelectedAsset(null);
        }}
        asset={selectedAsset}
      />

      {/* Add/Edit Subproject Modal */}
      {showSubprojectModal && (
        <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h2 className='modal-title'>{editingSubproject ? 'Edit Subproject' : 'Add Subproject'}</h2>
                <button 
                  type='button' 
                  className='btn-close' 
                  onClick={() => {
                    setShowSubprojectModal(false)
                    setEditingSubproject(null)
                    setSubprojectForm({ subproject_name: '', description: '', status: '1' })
                  }}
                ></button>
              </div>
              <div className='modal-body'>
                <form>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Subproject Name <span className='text-danger'>*</span></label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter subproject name'
                      value={subprojectForm.subproject_name}
                      onChange={(e) => setSubprojectForm(prev => ({...prev, subproject_name: e.target.value}))}
                      required
                    />
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Description</label>
                    <textarea
                      className='form-control'
                      rows={3}
                      placeholder='Enter subproject description'
                      value={subprojectForm.description}
                      onChange={(e) => setSubprojectForm(prev => ({...prev, description: e.target.value}))}
                    ></textarea>
                  </div>
                  <div className='mb-5'>
                    <label className='form-label fw-bold'>Status <span className='text-danger'>*</span></label>
                    <select
                      className='form-select'
                      value={subprojectForm.status}
                      onChange={(e) => setSubprojectForm(prev => ({...prev, status: e.target.value as '1' | '0'}))}
                    >
                      <option value='1'>Active</option>
                      <option value='0'>Inactive</option>
                    </select>
                  </div>
                </form>
              </div>
              <div className='modal-footer'>
                <button 
                  type='button' 
                  className='btn btn-light' 
                  onClick={() => {
                    setShowSubprojectModal(false)
                    setEditingSubproject(null)
                    setSubprojectForm({ subproject_name: '', description: '', status: '1' })
                  }}
                >
                  Cancel
                </button>
                <button 
                  type='button' 
                  className='btn btn-primary' 
                  onClick={handleSaveSubproject}
                  disabled={!subprojectForm.subproject_name.trim()}
                >
                  {editingSubproject ? 'Update Subproject' : 'Create Subproject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Subproject Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_subproject'
        tabIndex={-1}
        role='dialog'
        show={showDeleteSubprojectModal}
        onHide={() => {
          setShowDeleteSubprojectModal(false)
          setSubprojectToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteSubprojectModal(false)
                  setSubprojectToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Subproject</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this subproject?
                  {subprojectToDelete && (
                    <div className='mt-3'>
                      <strong className='text-dark'>"{subprojectToDelete.title || subprojectToDelete.subproject_name || 'Untitled'}"</strong>
                    </div>
                  )}
                  <br />This action cannot be undone.
                </div>
              </div>
              <div className='d-flex justify-content-end'>
                <button
                  type='button'
                  className='btn btn-light me-3'
                  onClick={() => {
                    setShowDeleteSubprojectModal(false)
                    setSubprojectToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-danger'
                  onClick={handleDeleteSubprojectConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>


    </div>
  )
}

export {ProjectDetail}
